import { FC, useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { FilePdfOutlined } from '@ant-design/icons';
import { Alert, Input, message, Tabs, Modal, Progress, Spin, Typography } from 'antd';

import { FormButtons } from '../../../../../../../lib/components/Form/FormButtons';
import { FormContainer } from '../../../../../../../lib/components/Form/FormContainer';
import { FormField } from '../../../../../../../lib/components/Form/FormField';
import { MultipleTagSelect } from '../../../../../../../lib/components/MultipleTagSelect';
import { UploaderPDF } from '../../../../../../../lib/components/UploaderPDF';
import { useRequestFeedback } from '../../../../../../../lib/hooks';
import { useSearchableApi } from '../../../../../../../lib/components/SearchableDataTable/hooks/useSearchableApi';
import { Tag } from '../../../../../../../lib/model/Tag';
import {
	ChapterEntity,
	ChapterSubTypesPosTextual,
	ChapterSubTypesPreTextual,
	ChapterTypes,
	Languages,
	SignedUrlTypes,
} from '../../../../../../../lib/model/Book';
import { extract_num_id_from_id } from '../../../../utils/extract_num_id_from_id';
import { InsertChapterDto } from '../../dto/InsertChapterDto';
import { UpdateChapterDto } from '../../dto/UpdateChapterDto';
import { ChapterItemDto } from '../../../../dto/ChapterItemDto';
import { useChaptersApi } from '../../hooks/useChaptersApi';

import { SelectChapterLanguage } from '../SelectChapterLanguage';
import { SelectChapterType } from '../SelectChapterType';
import { SelectChapterSubType } from '../SelectChapterSubType';
import { TextFromPdfModal } from '../TextFromPdfModal';
import { ChapterItemsTree } from '../ChapterItemsTree';
import { useChapterForm } from './useChapterForm';
import { pollOcrStatus } from './ocrPolling';

const { Text } = Typography;
const { TabPane } = Tabs;
const class_name_field = 'w-1/2';

interface IProps {
	chapter?: ChapterEntity;
	on_change_form?: () => void;
	is_published?: boolean;
}

type MetadataItem = {
	order: number;
	page?: number;
	digitalPage?: number;
	title: string;
	children?: MetadataItem[];
};

export const ChapterForm: FC<IProps> = ({ chapter, on_change_form, is_published }) => {
	const navigate = useNavigate();
	const location = useLocation();

	const {
		id: book_id,
		edition_id,
		chapter_id,
	} = useParams() as { id: string; edition_id: string; chapter_id: string };

	const book_num_id = extract_num_id_from_id(book_id);
	const edition_num_id = extract_num_id_from_id(edition_id);
	const chapter_num_id = extract_num_id_from_id(chapter_id);
	const BASE_URL = `${process.env.REACT_APP_BOOKS_API}/books/${book_num_id}/editions/${edition_num_id}/chapters/${chapter_num_id}/pdf`;

	const { form_state, show_errors, change_field_value, fill_with_data } = useChapterForm();
	const { insert, update, get_last_id, read_pdf, generate_metadata, get_metadata } = useChaptersApi();
	const searchAuthorApi = useSearchableApi<Tag>('admin-search-tags-entity');

	const [sub_type, set_sub_type] = useState('');
	const [activeTab, setActiveTab] = useState('infos');
	const [processing, setProcessing] = useState({
		visible: false,
		step: '',
		description: '',
		progress: 0,
	});

	const isChapter = form_state.value.chapter_type === ChapterTypes.CHAPTER;

	console.log(isChapter, '  <isChapter>  ');

	// 1. RECUPERA OS DADOS PASSADOS VIA NAVIGATE STATE (NOVO REGISTRO PRÉ/PÓS)
	useEffect(() => {
		if (!chapter && location.state?.chapterType) {
			const { initialTitle, chapterType, order } = location.state;

			fill_with_data({
				...form_state.value,
				chapter_type: chapterType,
				title: initialTitle || '',
				order: order ?? form_state.value.order,
			});

			if (chapterType !== ChapterTypes.CHAPTER) {
				set_sub_type(initialTitle || '');
			}

			setActiveTab('infos');
		}
	}, [location.state, chapter]);

	// Define aba inicial padrão com base no tipo
	useEffect(() => {
		if (isChapter) {
			setActiveTab('pdf');
		} else {
			setActiveTab('infos');
		}
	}, [isChapter, chapter]);

	function updateProcessing(step: string, description: string, progress: number) {
		setProcessing({
			visible: true,
			step,
			description,
			progress,
		});
	}

	function gerarNumeroAleatorio(min: number, max: number): number {
		return Math.floor(Math.random() * (max - min + 1)) + min;
	}

	function normalizeChapterType(type?: string): ChapterTypes {
		switch ((type || '').toLowerCase()) {
			case 'capitulo':
			case 'chapter':
				return ChapterTypes.CHAPTER;
			case 'pre_textual':
				return ChapterTypes.PRE_TEXTUAL;
			case 'pos_textual':
			case 'post_textual':
				return ChapterTypes.POS_TEXTUAL;
			default:
				return ChapterTypes.CHAPTER;
		}
	}

	function convertItem(item: MetadataItem): ChapterItemDto {
		return {
			title: item.title,
			page: item.page ?? 1,
			digitalPage: item.digitalPage ?? 1,
			order: item.order,
			items: item.children && item.children.length > 0 ? item.children.map(convertItem) : undefined,
		};
	}

	function convertMetadataToItems(items: MetadataItem[] = []): ChapterItemDto[] {
		return items.map(convertItem);
	}

	async function loadAuthors(authorNames: string[]): Promise<Tag[]> {
		const authors: Tag[] = [];
		for (const authorName of authorNames) {
			const response = await searchAuthorApi.search({
				text: authorName,
				tags: 'author',
			});

			const entities: Tag[] = response.entities ?? [];
			const found = entities.find(
				(tag) => tag.title.trim().toLowerCase() === authorName.trim().toLowerCase()
			);

			if (found) {
				authors.push(found);
			}
		}
		return authors;
	}

	async function processMetadata(metadata: any) {
		const authors = await loadAuthors(metadata.authors ?? []);

		fill_with_data({
			...form_state.value,
			title: normalizeChapterType(metadata.chapter_type) === ChapterTypes.CHAPTER ? metadata.title : '',
			chapter_type: normalizeChapterType(metadata.chapter_type),
			authors,
			items: convertMetadataToItems(metadata.tableOfContents ?? []),
			language: metadata.language ?? form_state.value.language,
		});

		updateProcessing('Concluído', 'Documento processado com sucesso', 100);

		setTimeout(() => {
			setProcessing({
				visible: false,
				step: '',
				description: '',
				progress: 0,
			});
			setActiveTab('infos');
			message.success('Documento processado. Confira as informações antes de salvar.');
		}, 700);
	}

	const processUploadedPdf = async () => {
		try {
			updateProcessing('Enviando PDF...', 'Fazendo upload do documento', gerarNumeroAleatorio(0, 24));
			await read_pdf.get();

			updateProcessing(
				'Extraindo metadados...',
				'Identificando título, autores e tipo',
				gerarNumeroAleatorio(30, 48)
			);
			await generate_metadata.generate();

			updateProcessing(
				'Montando estrutura...',
				'Obtendo sumário e informações do capítulo',
				gerarNumeroAleatorio(57, 78)
			);
			await get_metadata.get();

			updateProcessing('Finalizando...', 'Atualizando formulário', gerarNumeroAleatorio(83, 97));
		} catch (err) {
			console.error(err);
			setProcessing({
				visible: false,
				step: '',
				description: '',
				progress: 0,
			});
			message.error('Erro ao processar documento.');
		}
	};

	// Atualização dos campos via PDF / Metadados
	useEffect(() => {
		const pdf_result_key = (read_pdf.result as any)?.key;
		if (pdf_result_key) {
			change_field_value('pdf', pdf_result_key);
		}
	}, [read_pdf.result]);

	useEffect(() => {
		if (!get_metadata.result) return;
		processMetadata(get_metadata.result);
	}, [get_metadata.result]);

	// Carregamento de capítulo existente
	useEffect(() => {
		if (!chapter) {
			get_last_id.get();
			return;
		}

		let options: any[] = [];
		if (chapter.chapter_type === ChapterTypes.PRE_TEXTUAL) {
			options = Object.entries(ChapterSubTypesPreTextual).map(([key, value]) => ({ key, value }));
		}
		if (chapter.chapter_type === ChapterTypes.POS_TEXTUAL) {
			options = Object.entries(ChapterSubTypesPosTextual).map(([key, value]) => ({ key, value }));
		}

		const current_sub_type = options.find((o) => o.value === chapter.title);
		set_sub_type(current_sub_type?.value || '');

		fill_with_data({
			...form_state.value,
			authors: chapter.authors || [],
			language: chapter.language || Languages.PORTUGUESE,
			num_id: chapter.num_id || '',
			order: chapter.order || 1,
			title:
				(chapter.chapter_type === ChapterTypes.PRE_TEXTUAL ||
					chapter.chapter_type === ChapterTypes.POS_TEXTUAL) &&
				current_sub_type
					? ''
					: chapter.title || '',
			chapter_type: chapter.chapter_type || ChapterTypes.CHAPTER,
			pdf: chapter.pdf || '',
			items: chapter.items && chapter.items.length > 0 ? chapter.items : form_state.value.items,
		});
	}, [chapter]);

	// Obtenção do próximo num_id quando for um novo cadastro
	useEffect(() => {
		if (get_last_id.result && !form_state.value.num_id) {
			const last_num_id = Number(String(get_last_id.result.num_id).replace(/ /g, ''));
			fill_with_data({
				...form_state.value,
				num_id: last_num_id ? `${last_num_id + 1}` : '',
			});
		}
	}, [get_last_id.result]);

	// Reseta subtipo caso selecione "Capítulo"
	useEffect(() => {
		if (isChapter) {
			set_sub_type('');
		}
	}, [isChapter]);

	// Submissão do formulário
	function submit() {
		show_errors();

		let title = form_state?.value?.title || '';
		if (!isChapter && !form_state.value.title) {
			title = sub_type;
		}

		if (!form_state.valid) {
			return;
		}

		const now = new Date();
		const dto: UpdateChapterDto | InsertChapterDto = {
			...form_state.value,
			num_id: String(form_state.value.num_id),
			title,
			createDate: chapter?.createDate ?? now,
			alterDate: now,
			deleted: chapter?.deleted ?? false,
		};

		return chapter ? update.do_update(dto) : insert.create(dto);
	}

	// Feedback do salvamento/atualização
	useEffect(() => {
		const result = update.result || insert.result;
		if (result) {
			message.success('Salvo com sucesso.');
			if (result.pdf) {
				pollOcrStatus(result.num_id, book_num_id, edition_num_id);
			}
			navigate(`/conteudos/livros/${book_id}/edicoes/${edition_id}/capitulos/${result.id}`, {
				replace: true,
			});
		}
	}, [update.result, insert.result]);

	useRequestFeedback(update.result, update.error);
	useRequestFeedback(insert.result, insert.error);

	return (
		<>
			<Modal
				open={processing.visible}
				footer={null}
				closable={false}
				maskClosable={false}
				centered
				width={500}
			>
				<div style={{ textAlign: 'center', padding: 30 }}>
					<Spin size='large' />
					<div style={{ marginTop: 20 }}>
						<h3>{processing.step}</h3>
						<Text type='secondary'>{processing.description}</Text>
					</div>
					<div style={{ marginTop: 25 }}>
						<Progress percent={processing.progress} status='active' />
					</div>
				</div>
			</Modal>

			<Tabs type='card' activeKey={activeTab} onChange={setActiveTab}>
				{/* Exibe a aba de PDF apenas para tipo CHAPTER */}
				{isChapter && (
					<TabPane tab='PDF' key='pdf'>
						{chapter ? (
							<>
								<FormField {...{ label: 'PDF', name: 'pdf', form_state }}>
									<UploaderPDF
										on_success={processUploadedPdf}
										upload_api_endpoint={`${BASE_URL}/${SignedUrlTypes.WRITE}`}
									/>
								</FormField>
								{chapter.pdf && (
									<FormField {...{ label: 'Download', name: 'key', required: false, form_state }}>
										<a
											href={chapter.pdf}
											target='_blank'
											rel='noopener noreferrer'
											className='block mt-2 mb-1'
										>
											<div className='flex items-center'>
												<FilePdfOutlined style={{ marginRight: '0.3rem' }} />{' '}
												{String(chapter.key).split('/')[3]}
											</div>
										</a>
									</FormField>
								)}
								<FormField
									{...{ label: 'Texto do Capítulo', name: 'txt', required: false, form_state }}
								>
									<TextFromPdfModal formState={form_state} />
								</FormField>
							</>
						) : (
							<FormField {...{ label: 'PDF', name: 'pdf', form_state }}>
								<UploaderPDF
									on_success={processUploadedPdf}
									upload_api_endpoint={`${BASE_URL}/${SignedUrlTypes.WRITE}`}
								/>
							</FormField>
						)}
					</TabPane>
				)}

				<TabPane tab='Informações' key='infos'>
					{isChapter && (
						<Alert
							type='info'
							showIcon
							message='Os dados abaixo foram preenchidos automaticamente.'
							description='Confira as informações antes de salvar o capítulo.'
						/>
					)}
					<br />

					<FormContainer>
						{chapter && (
							<>
								<Alert
									message='É necessário republicar a edição para que as alterações reflitam na plataforma.'
									type='warning'
								/>
								<br />
							</>
						)}

						<FormField {...{ label: 'ID', name: 'num_id', form_state }}>
							<Input
								value={form_state.value.num_id}
								type='text'
								onChange={(event) => change_field_value('num_id', event.target.value)}
								className={class_name_field}
								disabled
							/>
						</FormField>

						<FormField {...{ label: 'Tipo', name: 'type', form_state }}>
							<SelectChapterType
								value={form_state.value.chapter_type}
								on_change={change_field_value}
							/>
						</FormField>

						{!isChapter && (
							<FormField {...{ label: 'Subtipo', name: 'sub_type', form_state }}>
								<SelectChapterSubType
									type={form_state.value.chapter_type}
									value={sub_type}
									on_change={set_sub_type}
								/>
							</FormField>
						)}

						<FormField
							{...{
								label: 'Título',
								name: 'title',
								form_state,
								required: isChapter,
							}}
						>
							<Input
								value={form_state.value.title}
								onChange={(event) => change_field_value('title', event.target.value)}
								className={class_name_field}
							/>
						</FormField>

						{chapter && isChapter && (
							<FormField
								{...{
									label: 'Intertítulos (Sumário do capítulo)',
									name: 'items',
									required: false,
									form_state,
								}}
							>
								<ChapterItemsTree
									value={form_state.value.items || []}
									onChange={(items) => change_field_value('items', items)}
								/>
							</FormField>
						)}

						<FormField {...{ label: 'Ordem de Listagem', name: 'order', form_state }}>
							<Input
								value={form_state.value.order}
								type='number'
								onChange={(event) => change_field_value('order', event.target.value)}
								className={class_name_field}
							/>
						</FormField>

						<FormField {...{ label: 'Idioma', name: 'language', form_state }}>
							<SelectChapterLanguage
								value={form_state.value.language}
								on_change={change_field_value}
							/>
						</FormField>

						<div className={class_name_field}>
							<MultipleTagSelect
								{...{
									form_state,
									label: 'Autores',
									name: 'authors',
									required: false,
									on_change: (authors) =>
										change_field_value(
											'authors',
											authors.map((author) => ({ id: author.id, title: author.title }))
										),
									values: form_state.value.authors,
									tag_prefix: 'author',
								}}
							/>
						</div>

						<FormButtons
							{...{
								disabled: !form_state.valid,
								on_update_button_click: chapter ? submit : undefined,
								go_back_on_cancel: true,
								on_add_button_click: !chapter ? submit : undefined,
							}}
						/>
					</FormContainer>
				</TabPane>
			</Tabs>
		</>
	);
};
// import { FC, useEffect, useState } from 'react';
// import { useNavigate, useParams } from 'react-router-dom';
// import { FormButtons } from '../../../../../../../lib/components/Form/FormButtons';
// import { FormContainer } from '../../../../../../../lib/components/Form/FormContainer';
// import { FormField } from '../../../../../../../lib/components/Form/FormField';
// import { MultipleTagSelect } from '../../../../../../../lib/components/MultipleTagSelect';
// import { UploaderPDF } from '../../../../../../../lib/components/UploaderPDF';
// import { useRequestFeedback } from '../../../../../../../lib/hooks';
// import {
// 	ChapterEntity,
// 	ChapterSubTypesPosTextual,
// 	ChapterSubTypesPreTextual,
// 	ChapterTypes,
// 	Languages,
// 	SignedUrlTypes,
// } from '../../../../../../../lib/model/Book';
// import { extract_num_id_from_id } from '../../../../utils/extract_num_id_from_id';
// import { InsertChapterDto } from '../../dto/InsertChapterDto';
// import { UpdateChapterDto } from '../../dto/UpdateChapterDto';
// import { useChaptersApi } from '../../hooks/useChaptersApi';
// import { SelectChapterLanguage } from '../SelectChapterLanguage';
// import { SelectChapterType } from '../SelectChapterType';
// import { TextFromPdfModal } from '../TextFromPdfModal';
// import { useChapterForm } from './useChapterForm';
// import { FilePdfOutlined } from '@ant-design/icons';
// import { SelectChapterSubType } from '../SelectChapterSubType';
// import { pollOcrStatus } from './ocrPolling';

// import { ChapterItemsTree } from '../ChapterItemsTree';
// import { ChapterItemDto } from '../../../../dto/ChapterItemDto';
// import { useSearchableApi } from '../../../../../../../lib/components/SearchableDataTable/hooks/useSearchableApi';
// import { Tag } from '../../../../../../../lib/model/Tag';
// import { Alert, Input, message, Tabs, Modal, Progress, Spin, Typography } from 'antd';

// const { Text } = Typography;

// const class_name_field = 'w-1/2';

// interface IProps {
// 	chapter?: ChapterEntity;
// 	on_change_form?: () => void;
// 	is_published?: boolean;
// }

// export const ChapterForm: FC<IProps> = ({ chapter, on_change_form, is_published }) => {
// 	const navigate = useNavigate();
// 	const { TabPane } = Tabs;
// 	const {
// 		id: book_id,
// 		edition_id,
// 		chapter_id,
// 	} = useParams() as { id: string; edition_id: string; chapter_id: string };
// 	const book_num_id = extract_num_id_from_id(book_id);
// 	const edition_num_id = extract_num_id_from_id(edition_id);
// 	const chapter_num_id = extract_num_id_from_id(chapter_id);
// 	const BASE_URL = `${process.env.REACT_APP_BOOKS_API}/books/${book_num_id}/editions/${edition_num_id}/chapters/${chapter_num_id}/pdf`;

// 	const { form_state, show_errors, change_field_value, fill_with_data } = useChapterForm();
// 	const [sub_type, set_sub_type] = useState('');
// 	const [activeTab, setActiveTab] = useState('pdf');
// 	const [processing, setProcessing] = useState({
// 		visible: false,
// 		step: '',
// 		description: '',
// 		progress: 0,
// 	});

// 	function updateProcessing(step: string, description: string, progress: number) {
// 		setProcessing({
// 			visible: true,
// 			step,
// 			description,
// 			progress,
// 		});
// 	}

// 	const { insert, update, get_last_id, read_pdf, generate_metadata, get_metadata } = useChaptersApi();

// 	const searchAuthorApi = useSearchableApi<Tag>('admin-search-tags-entity');

// 	async function loadAuthors(authorNames: string[]): Promise<Tag[]> {
// 		const authors: Tag[] = [];

// 		for (const authorName of authorNames) {
// 			const response = await searchAuthorApi.search({
// 				text: authorName,
// 				tags: 'author',
// 			});

// 			console.log(authorName, response);

// 			const entities: Tag[] = response.entities ?? [];

// 			const found = entities.find(
// 				(tag) => tag.title.trim().toLowerCase() === authorName.trim().toLowerCase()
// 			);

// 			if (found) {
// 				authors.push(found);
// 			}
// 		}

// 		return authors;
// 	}
// 	async function processMetadata(metadata: any) {
// 		const authors = await loadAuthors(metadata.authors ?? []);

// 		fill_with_data({
// 			...form_state.value,

// 			title: normalizeChapterType(metadata.chapter_type) === ChapterTypes.CHAPTER ? metadata.title : '',

// 			chapter_type: normalizeChapterType(metadata.chapter_type),

// 			authors,

// 			items: convertMetadataToItems(metadata.tableOfContents ?? []),

// 			language: metadata.language ?? form_state.value.language,
// 		});

// 		updateProcessing('Concluído', 'Documento processado com sucesso', 100);

// 		setTimeout(() => {
// 			setProcessing({
// 				visible: false,
// 				step: '',
// 				description: '',
// 				progress: 0,
// 			});

// 			setActiveTab('infos');

// 			message.success('Documento processado. Confira as informações antes de salvar.');
// 		}, 700);

// 		setActiveTab('infos');
// 	}

// 	useEffect(() => {
// 		const pdf_result_key = (read_pdf.result as any)?.key;
// 		if (pdf_result_key) {
// 			change_field_value('pdf', pdf_result_key);
// 		}
// 	}, [read_pdf.result]);

// 	useEffect(() => {
// 		if (!chapter) {
// 			get_last_id.get();
// 			return;
// 		}

// 		let options: any[] = [];

// 		if (chapter.chapter_type === ChapterTypes.PRE_TEXTUAL) {
// 			options = Object.entries(ChapterSubTypesPreTextual).map(([key, value]) => ({
// 				key,
// 				value,
// 			}));
// 		}

// 		if (chapter.chapter_type === ChapterTypes.POS_TEXTUAL) {
// 			options = Object.entries(ChapterSubTypesPosTextual).map(([key, value]) => ({
// 				key,
// 				value,
// 			}));
// 		}

// 		const current_sub_type = options.find((o) => o.value === chapter.title);

// 		set_sub_type(current_sub_type || '');

// 		fill_with_data({
// 			...form_state.value,

// 			authors: chapter.authors || [],
// 			language: chapter.language || Languages.PORTUGUESE,
// 			num_id: chapter.num_id || '',
// 			order: chapter.order || 1,

// 			title:
// 				(chapter.chapter_type === ChapterTypes.PRE_TEXTUAL ||
// 					chapter.chapter_type === ChapterTypes.POS_TEXTUAL) &&
// 				current_sub_type
// 					? ''
// 					: chapter.title || '',

// 			chapter_type: chapter.chapter_type || ChapterTypes.CHAPTER,

// 			pdf: chapter.pdf || '',

// 			// IMPORTANTE
// 			items: chapter.items && chapter.items.length > 0 ? chapter.items : form_state.value.items,
// 		});
// 	}, [chapter]);
// 	function submit() {
// 		show_errors();

// 		let title = form_state?.value?.title || '';

// 		if (form_state.value.chapter_type !== ChapterTypes.CHAPTER && !form_state.value.title) {
// 			title = sub_type;
// 		}

// 		if (!form_state.valid) {
// 			return;
// 		}

// 		const now = new Date();

// 		const dto: UpdateChapterDto | InsertChapterDto = {
// 			...form_state.value,
// 			num_id: String(form_state.value.num_id),
// 			title,

// 			createDate: chapter?.createDate ?? now,
// 			alterDate: now,
// 			deleted: chapter?.deleted ?? false,
// 		};

// 		return chapter ? update.do_update(dto) : insert.create(dto);
// 	}
// 	useEffect(() => {
// 		const result = update.result || insert.result;
// 		if (result) {
// 			message.success('Salvo com sucesso.');
// 			if (result.pdf) {
// 				pollOcrStatus(result.num_id, book_num_id, edition_num_id);
// 			}
// 			navigate(`/conteudos/livros/${book_id}/edicoes/${edition_id}/capitulos/${result.id}`, {
// 				replace: true,
// 			});
// 		}
// 	}, [update.result, insert.result]);

// 	useRequestFeedback(update.result, update.error);
// 	useRequestFeedback(insert.result, insert.error);

// 	useEffect(() => {
// 		if (get_last_id.result) {
// 			const last_num_id = Number(String(get_last_id.result.num_id).replace(/ /g, ''));
// 			if (!form_state.value.num_id) {
// 				fill_with_data({
// 					...form_state.value,

// 					num_id: last_num_id ? `${last_num_id + 1}` : '',
// 				});
// 			}
// 		}
// 	}, [get_last_id.result]);

// 	function normalizeChapterType(type?: string): ChapterTypes {
// 		switch ((type || '').toLowerCase()) {
// 			case 'capitulo':
// 			case 'chapter':
// 				return ChapterTypes.CHAPTER;

// 			case 'pre_textual':
// 				return ChapterTypes.PRE_TEXTUAL;

// 			case 'pos_textual':
// 			case 'post_textual':
// 				return ChapterTypes.POS_TEXTUAL;

// 			default:
// 				return ChapterTypes.CHAPTER;
// 		}
// 	}

// 	useEffect(() => {
// 		if (!get_metadata.result) return;

// 		processMetadata(get_metadata.result);
// 	}, [get_metadata.result]);

// 	type MetadataItem = {
// 		order: number;
// 		page?: number;
// 		title: string;
// 		children?: MetadataItem[];
// 	};

// 	function convertMetadataToItems(items: MetadataItem[] = []): ChapterItemDto[] {
// 		return items.map(convertItem);
// 	}

// 	function convertItem(item: MetadataItem): ChapterItemDto {
// 		return {
// 			title: item.title,
// 			page: item.page ?? 1,
// 			order: item.order,
// 			items: item.children && item.children.length > 0 ? item.children.map(convertItem) : undefined,
// 		};
// 	}

// 	const isChapter = form_state.value.chapter_type === ChapterTypes.CHAPTER;

// 	console.log(isChapter, ' <isChapter>  ');

// 	useEffect(() => {
// 		if (form_state.value.chapter_type === ChapterTypes.CHAPTER) {
// 			set_sub_type('');
// 		}
// 	}, [form_state.value.chapter_type]);

// 	const processUploadedPdf = async () => {
// 		try {
// 			updateProcessing('Enviando PDF...', 'Fazendo upload do documento', gerarNumeroAleatorio(0, 24));

// 			await read_pdf.get();

// 			updateProcessing(
// 				'Extraindo metadados...',
// 				'Identificando título, autores e tipo',
// 				gerarNumeroAleatorio(30, 48)
// 			);

// 			await generate_metadata.generate();

// 			updateProcessing(
// 				'Montando estrutura...',
// 				'Obtendo sumário e informações do capítulo',
// 				gerarNumeroAleatorio(57, 78)
// 			);

// 			await get_metadata.get();

// 			updateProcessing('Finalizando...', 'Atualizando formulário', gerarNumeroAleatorio(83, 97));
// 		} catch (err) {
// 			console.error(err);

// 			setProcessing({
// 				visible: false,
// 				step: '',
// 				description: '',
// 				progress: 0,
// 			});

// 			message.error('Erro ao processar documento.');
// 		}
// 	};

// 	function gerarNumeroAleatorio(min: number, max: number): number {
// 		return Math.floor(Math.random() * (max - min + 1)) + min;
// 	}

// 	return (
// 		<>
// 			<Modal
// 				open={processing.visible}
// 				footer={null}
// 				closable={false}
// 				maskClosable={false}
// 				centered
// 				width={500}
// 			>
// 				<div
// 					style={{
// 						textAlign: 'center',
// 						padding: 30,
// 					}}
// 				>
// 					<Spin size='large' />

// 					<div style={{ marginTop: 20 }}>
// 						<h3>{processing.step}</h3>

// 						<Text type='secondary'>{processing.description}</Text>
// 					</div>

// 					<div style={{ marginTop: 25 }}>
// 						<Progress percent={processing.progress} status='active' />
// 					</div>
// 				</div>
// 			</Modal>
// 			{isChapter} (
// 			<>
// 				<Tabs type='card' activeKey={activeTab} onChange={setActiveTab}>
// 					<TabPane tab='PDF' key='pdf'>
// 						{chapter ? (
// 							<>
// 								<FormField {...{ label: 'PDF', name: 'pdf', form_state }}>
// 									<UploaderPDF
// 										on_success={processUploadedPdf}
// 										upload_api_endpoint={`${BASE_URL}/${SignedUrlTypes.WRITE}`}
// 									/>
// 								</FormField>
// 								{chapter.pdf && (
// 									<FormField {...{ label: 'Download', name: 'key', required: false, form_state }}>
// 										<a
// 											href={chapter.pdf}
// 											target='_blank'
// 											rel='noopener noreferrer'
// 											className='block mt-2 mb-1'
// 										>
// 											<div className='flex items-center'>
// 												<FilePdfOutlined style={{ marginRight: '0.3rem' }} />{' '}
// 												{String(chapter.key).split('/')[3]}
// 											</div>
// 										</a>
// 									</FormField>
// 								)}
// 								<FormField
// 									{...{ label: 'Texto do Capítulo', name: 'txt', required: false, form_state }}
// 								>
// 									<TextFromPdfModal formState={form_state} />
// 								</FormField>
// 							</>
// 						) : (
// 							<>
// 								<FormField {...{ label: 'PDF', name: 'pdf', form_state }}>
// 									<UploaderPDF
// 										on_success={processUploadedPdf}
// 										upload_api_endpoint={`${BASE_URL}/${SignedUrlTypes.WRITE}`}
// 									/>
// 								</FormField>
// 								{/* <FormField {...{ label: 'Texto do Capítulo', name: 'txt', required: false, form_state }}>
// 								<TextFromPdfModal formState={form_state} />
// 							</FormField> */}
// 							</>
// 						)}
// 					</TabPane>
// 					<TabPane tab='Informações' key='infos'>
// 						<Alert
// 							type='info'
// 							showIcon
// 							message='Os dados abaixo foram preenchidos automaticamente.'
// 							description='Confira as informações antes de salvar o capítulo.'
// 						/>

// 						<br />
// 						<FormContainer>
// 							{chapter ? (
// 								<>
// 									<Alert
// 										message={`É necessário republicar a edição para que as alterações reflitam na plataforma.`}
// 										type='warning'
// 									/>
// 									<br />
// 								</>
// 							) : (
// 								<></>
// 							)}
// 							<FormField {...{ label: 'ID', name: 'num_id', form_state }}>
// 								<Input
// 									value={form_state.value.num_id}
// 									type={'text'}
// 									onChange={(event) => change_field_value('num_id', event.target.value)}
// 									className={class_name_field}
// 									disabled={true}
// 								/>
// 							</FormField>

// 							<FormField {...{ label: 'Tipo', name: 'type', form_state }}>
// 								<SelectChapterType
// 									{...{ value: form_state.value.chapter_type, on_change: change_field_value }}
// 								/>
// 							</FormField>

// 							{!isChapter && (
// 								<FormField {...{ label: 'Subtipo', name: 'sub_type', form_state }}>
// 									<SelectChapterSubType
// 										type={form_state.value.chapter_type}
// 										value={sub_type}
// 										on_change={set_sub_type}
// 									/>
// 								</FormField>
// 							)}

// 							<FormField
// 								{...{
// 									label: 'Título',
// 									name: 'title',
// 									form_state,
// 									required: form_state.value.chapter_type === ChapterTypes.CHAPTER,
// 								}}
// 							>
// 								<Input
// 									value={form_state.value.title}
// 									onChange={(event) => change_field_value('title', event.target.value)}
// 									className={class_name_field}
// 								/>
// 							</FormField>
// 							{/* mudar para tratar is_published */}
// 							{chapter && true && (
// 								<FormField
// 									{...{
// 										label: 'Intertítulos (Sumário do capítulo)',
// 										name: 'items',
// 										required: false,
// 										form_state,
// 									}}
// 								>
// 									<ChapterItemsTree
// 										value={form_state.value.items || []}
// 										onChange={(items) => change_field_value('items', items)}
// 									/>
// 								</FormField>
// 							)}

// 							<FormField {...{ label: 'Ordem de Listagem', name: 'order', form_state }}>
// 								<Input
// 									value={form_state.value.order}
// 									type={'number'}
// 									onChange={(event) => change_field_value('order', event.target.value)}
// 									className={class_name_field}
// 								/>
// 							</FormField>

// 							<FormField {...{ label: 'Idioma', name: 'language', form_state }}>
// 								<SelectChapterLanguage
// 									{...{ value: form_state.value.language, on_change: change_field_value }}
// 								/>
// 							</FormField>

// 							<div className={class_name_field}>
// 								<MultipleTagSelect
// 									{...{
// 										form_state,
// 										label: 'Autores',
// 										name: 'authors',
// 										required: false,
// 										on_change: (authors) =>
// 											change_field_value(
// 												'authors',
// 												authors.map((author) => ({ id: author.id, title: author.title }))
// 											),
// 										values: form_state.value.authors,
// 										tag_prefix: 'author',
// 									}}
// 								/>
// 							</div>

// 							<FormButtons
// 								{...{
// 									disabled: !form_state.valid,
// 									on_update_button_click: chapter ? submit : undefined,
// 									go_back_on_cancel: true,
// 									on_add_button_click: !chapter ? submit : undefined,
// 								}}
// 							/>
// 						</FormContainer>
// 					</TabPane>
// 				</Tabs>
// 			</>
// 			){!isChapter}(
// 			<>
// 				<Tabs type='card'>
// 					<TabPane tab='Informações' key='infos'>
// 						<FormContainer>
// 							{chapter ? (
// 								<>
// 									<Alert
// 										message={`É necessário republicar a edição para que as alterações reflitam na plataforma.`}
// 										type='warning'
// 									/>
// 									<br />
// 								</>
// 							) : (
// 								<></>
// 							)}
// 							<FormField {...{ label: 'ID', name: 'num_id', form_state }}>
// 								<Input
// 									value={form_state.value.num_id}
// 									type={'text'}
// 									onChange={(event) => change_field_value('num_id', event.target.value)}
// 									className={class_name_field}
// 									disabled={true}
// 								/>
// 							</FormField>

// 							<FormField {...{ label: 'Tipo', name: 'type', form_state }}>
// 								<SelectChapterType
// 									{...{ value: form_state.value.chapter_type, on_change: change_field_value }}
// 								/>
// 							</FormField>

// 							{form_state.value.chapter_type !== ChapterTypes.CHAPTER ? (
// 								<FormField {...{ label: 'Subtipo', name: 'sub_type', form_state }}>
// 									<SelectChapterSubType
// 										{...{
// 											type: form_state.value.chapter_type,
// 											value: sub_type,
// 											on_change: set_sub_type,
// 										}}
// 									/>
// 								</FormField>
// 							) : (
// 								<></>
// 							)}

// 							<FormField
// 								{...{
// 									label: 'Título',
// 									name: 'title',
// 									form_state,
// 									required: form_state.value.chapter_type === ChapterTypes.CHAPTER,
// 								}}
// 							>
// 								<Input
// 									value={form_state.value.title}
// 									onChange={(event) => change_field_value('title', event.target.value)}
// 									className={class_name_field}
// 								/>
// 							</FormField>
// 							{/* mudar para tratar is_published */}
// 							{chapter && true && (
// 								<FormField
// 									{...{
// 										label: 'Intertítulos (Sumário do capítulo)',
// 										name: 'items',
// 										required: false,
// 										form_state,
// 									}}
// 								>
// 									<ChapterItemsTree
// 										value={form_state.value.items || []}
// 										onChange={(items) => change_field_value('items', items)}
// 									/>
// 								</FormField>
// 							)}

// 							<FormField {...{ label: 'Ordem de Listagem', name: 'order', form_state }}>
// 								<Input
// 									value={form_state.value.order}
// 									type={'number'}
// 									onChange={(event) => change_field_value('order', event.target.value)}
// 									className={class_name_field}
// 								/>
// 							</FormField>

// 							<FormField {...{ label: 'Idioma', name: 'language', form_state }}>
// 								<SelectChapterLanguage
// 									{...{ value: form_state.value.language, on_change: change_field_value }}
// 								/>
// 							</FormField>

// 							<div className={class_name_field}>
// 								<MultipleTagSelect
// 									{...{
// 										form_state,
// 										label: 'Autores',
// 										name: 'authors',
// 										required: false,
// 										on_change: (authors) =>
// 											change_field_value(
// 												'authors',
// 												authors.map((author) => ({ id: author.id, title: author.title }))
// 											),
// 										values: form_state.value.authors,
// 										tag_prefix: 'author',
// 									}}
// 								/>
// 							</div>

// 							<FormButtons
// 								{...{
// 									disabled: !form_state.valid,
// 									on_update_button_click: chapter ? submit : undefined,
// 									go_back_on_cancel: true,
// 									on_add_button_click: !chapter ? submit : undefined,
// 								}}
// 							/>
// 						</FormContainer>
// 					</TabPane>
// 					<TabPane tab='PDF' key='pdf'>
// 						{chapter ? (
// 							<>
// 								<FormField {...{ label: 'PDF', name: 'pdf', form_state }}>
// 									<UploaderPDF
// 										{...{
// 											on_success: () => {
// 												message.success(
// 													'Upload do documento concluído. O sistema irá buscar os dados do arquivo.'
// 												);
// 												read_pdf.get();
// 												if (on_change_form) on_change_form();
// 											},
// 											upload_api_endpoint: `${BASE_URL}/${SignedUrlTypes.WRITE}`,
// 										}}
// 									/>
// 								</FormField>
// 								{chapter.pdf && (
// 									<FormField {...{ label: 'Download', name: 'key', required: false, form_state }}>
// 										<a
// 											href={chapter.pdf}
// 											target='_blank'
// 											rel='noopener noreferrer'
// 											className='block mt-2 mb-1'
// 										>
// 											<div className='flex items-center'>
// 												<FilePdfOutlined style={{ marginRight: '0.3rem' }} />{' '}
// 												{String(chapter.key).split('/')[3]}
// 											</div>
// 										</a>
// 									</FormField>
// 								)}
// 								<FormField
// 									{...{ label: 'Texto do Capítulo', name: 'txt', required: false, form_state }}
// 								>
// 									<TextFromPdfModal formState={form_state} />
// 								</FormField>
// 							</>
// 						) : (
// 							<div className='block'>
// 								Por favor, salve os dados iniciais antes de realizar o upload de um PDF.
// 							</div>
// 						)}
// 					</TabPane>
// 				</Tabs>
// 			</>
// 			)
// 		</>
// 	);
// };
